export interface Coordinates {
  lat: number;
  lng: number;
}

const inRange = (lat: number, lng: number): Coordinates | null =>
  Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180
    ? { lat, lng }
    : null;

// 25°35'15.4"N 73°53'31.6"E — what Google writes for a place searched by its degrees
const DEGREES =
  /(\d{1,3})\s*°\s*(\d{1,2})\s*['′]\s*(\d{1,2}(?:\.\d+)?)\s*["″]?\s*([NS])[\s,]+(\d{1,3})\s*°\s*(\d{1,2})\s*['′]\s*(\d{1,2}(?:\.\d+)?)\s*["″]?\s*([EW])/i;
// 25.5876, 73.8921
const DECIMAL = /^\s*(-?\d{1,2}(?:\.\d+)?)\s*,\s*(-?\d{1,3}(?:\.\d+)?)\s*$/;

function coordinatesOfText(text: string): Coordinates | null {
  const degrees = DEGREES.exec(text);
  if (degrees) {
    const [, latDeg, latMin, latSec, northSouth, lngDeg, lngMin, lngSec, eastWest] = degrees;
    if (
      Number(latMin) >= 60 ||
      Number(latSec) >= 60 ||
      Number(lngMin) >= 60 ||
      Number(lngSec) >= 60
    ) {
      return null;
    }
    const lat = Number(latDeg) + Number(latMin) / 60 + Number(latSec) / 3600;
    const lng = Number(lngDeg) + Number(lngMin) / 60 + Number(lngSec) / 3600;
    return inRange(
      northSouth?.toUpperCase() === 'S' ? -lat : lat,
      eastWest?.toUpperCase() === 'W' ? -lng : lng,
    );
  }
  const decimal = DECIMAL.exec(text);
  return decimal ? inRange(Number(decimal[1]), Number(decimal[2])) : null;
}

/** The text that was searched, from the embed's `!2z<base64>` entry; `null` when it is absent or not decodable. */
function searchedText(embedUrl: string): string | null {
  const raw = /!2z([^!&#]+)/.exec(embedUrl)?.[1];
  if (!raw) return null;
  try {
    const base64 = decodeURIComponent(raw).replace(/-/g, '+').replace(/_/g, '/');
    return new TextDecoder().decode(Uint8Array.from(atob(base64), (char) => char.charCodeAt(0)));
  } catch {
    return null;
  }
}

/**
 * The map pin of a Google Maps "Share → Embed a map" address; nothing here talks to Google. Its `pb`
 * parameter carries the *view* (`!2d<longitude>!3d<latitude>`), which Google nudges sideways to leave room for
 * the place card (about 250 m for the works), and the text that was *searched* (`!2z<base64>`). When that text
 * is itself a coordinate pair — `25°35'15.4"N 73°53'31.6"E` or `25.5876, 73.8921` — it is the exact pin and
 * wins; otherwise the centre of the view is the best there is.
 */
export function coordinatesOfEmbed(embedUrl: string | null | undefined): Coordinates | null {
  if (!embedUrl) return null;
  const text = searchedText(embedUrl);
  const searched = text === null ? null : coordinatesOfText(text);
  if (searched) return searched;
  const view = /!2d(-?\d{1,3}(?:\.\d+)?)!3d(-?\d{1,3}(?:\.\d+)?)/.exec(embedUrl);
  return view ? inRange(Number(view[2]), Number(view[1])) : null;
}

/**
 * Where "Open in Google Maps" / "Get directions" goes: the exact pin of the embedded map when there is one
 * (an address in a small industrial estate often does not geocode), else a search for the address, else
 * nothing. A link only — the visitor's map app or site opens it; nothing is embedded or sent from here.
 */
export function placeUrl(
  address: string | null | undefined,
  embedUrl: string | null | undefined,
): string | null {
  const pin = coordinatesOfEmbed(embedUrl);
  if (pin) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${pin.lat.toFixed(6)},${pin.lng.toFixed(6)}`)}`;
  }
  const text = address?.trim();
  return text
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(text)}`
    : null;
}
